import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, CheckCircle2, CalendarCheck, HardHat, LayoutDashboard,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PowerBuiltMark } from "@/components/PowerBuiltMark";

const VALUE_POINTS = [
  { icon: CalendarCheck,   label: "Book a consultation online" },
  { icon: HardHat,         label: "Follow your installation in real time" },
  { icon: LayoutDashboard, label: "Manage invoices, service and warranty in one place" },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setAuthFromResponse } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!email || !firstName) return;
    setLoading(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const r = await api.post("/auth/dev-login", {
        email: email.trim(),
        name: fullName,
        role: "client",
        phone: phone.trim() || undefined,
      });
      setAuthFromResponse(r.data);
      // New customers always land on the client portal; from there we walk
      // them through property + interests + consultation (progressive onboarding).
      navigate("/dashboard");
    } catch (err) {
      toast.error("We couldn't create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left — form */}
      <div className="flex flex-col justify-center px-8 md:px-16 py-12 bg-white order-2 md:order-1">
        <Link to="/" className="flex items-center gap-3 mb-10" data-testid="register-logo">
          <PowerBuiltMark className="w-11 h-11" darkOnLight />
          <div className="leading-tight">
            <div className="font-heading font-semibold tracking-wide text-[15px] text-[#0A2540]">
              PowerBuilt
            </div>
            <div className="text-[#94A3B8] text-[10px] tracking-[0.28em] uppercase">
              Solar · Electrical · Maintenance
            </div>
          </div>
        </Link>

        <div className="max-w-md">
          <p className="overline text-[#C9A356] mb-3">Create your account</p>
          <h1 className="text-4xl font-heading font-medium text-[#0A2540] tracking-tight leading-tight">
            Get started with PowerBuilt.
          </h1>
          <p className="mt-3 text-[#475569] leading-relaxed">
            One account to book a consultation, receive your personalized proposal, track your installation, and manage everything after.
          </p>

          <div className="mt-8 space-y-4">
            <button
              onClick={handleGoogleSignup}
              data-testid="google-signup-btn"
              className="w-full flex items-center justify-center gap-3 border-2 border-[#E2E8F0] hover:border-[#0A2540] rounded-md py-3.5 transition-all bg-white text-[#0A2540] font-medium"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]"></div></div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-[#475569] uppercase tracking-wider">or</span>
              </div>
            </div>

            <form onSubmit={handleEmailSignup} className="space-y-3" data-testid="register-form">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  required
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="register-first-name"
                  className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="register-last-name"
                  className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none"
                />
              </div>
              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="register-phone"
                className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none"
              />
              <button
                type="submit"
                disabled={loading || !email || !firstName}
                data-testid="register-submit"
                className="btn-accent w-full disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Create Account <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </button>
            </form>
          </div>

          <p className="mt-8 text-sm text-[#475569]">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="link-signin"
              className="font-medium text-[#0A2540] underline decoration-[#C9A356] decoration-2 underline-offset-4 hover:text-[#C9A356]"
            >
              Sign in
            </Link>
          </p>

          <p className="mt-6 text-xs text-[#94A3B8] leading-relaxed">
            We only ask for the basics. You'll add your property and project details after signing in.
          </p>
        </div>
      </div>

      {/* Right — brand + value */}
      <div className="hidden md:flex relative bg-[#0A2540] overflow-hidden order-1 md:order-2">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540] via-[#103960] to-[#0A2540]"></div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(201,163,86,0.18) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(201,163,86,0.15) 0%, transparent 50%)`,
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <PowerBuiltMark className="w-12 h-12" />
            <div className="leading-tight">
              <div className="font-heading font-semibold tracking-wide text-[15px] text-white">
                PowerBuilt
              </div>
              <div className="text-white/50 text-[10px] tracking-[0.28em] uppercase">
                Solar · Electrical · Maintenance
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <p className="overline text-[#C9A356] mb-4">Why an account?</p>
            <h2 className="font-heading text-4xl font-medium leading-tight">
              A private workspace<br />
              <span className="text-[#C9A356]">for your build.</span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Your PowerBuilt account is where every part of your project comes together — from first consultation to long-term service.
            </p>

            <ul className="mt-8 space-y-3.5">
              {VALUE_POINTS.map((v) => (
                <li key={v.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0">
                    <v.icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm text-white/90">{v.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
              <CheckCircle2 className="w-4 h-4 text-[#C9A356]" />
              Your data stays private to your account.
            </div>
          </div>

          <div className="text-xs text-white/40">
            Every new customer receives a unique PowerBuilt ID (PB-XXXXX).
          </div>
        </div>
      </div>
    </div>
  );
}
